import { Button, Modal, Password, Select, Text } from '@therms/atalaya'
import React, { useState } from 'react'
import { Alerts } from '../../../components/Alerts'
import {
  OrganizationGetOrganizationOkResponseBody,
  SuperuserDeactivateOrg,
  SuperuserGetAllOrgs,
  SuperuserGetOrgOwnerJwt,
  SuperuserSetPricing,
} from '../../../services/api-client'
import { superuserGetAllBillingOrgs } from '@src/generatedClient/generated/taskreportsApi'

interface LoginProps {
  onComplete: (jwt: string) => void
  setLoading: (loading: boolean) => void
}

function SuperuserLogin({ onComplete, setLoading }: LoginProps) {
  // hack to hide an organization nobody should be touching
  const hiddenFilter = (x: string): boolean => {
    if (
        x === 'ffb6a55e-9f81-46ce-8c0e-0aef7ce18b55' &&
        // @ts-ignore
        window.show_hidden !== true
    )
      return false
    return true
  }
  const [allOrganizations, setAllOrganizations] = useState<
      OrganizationGetOrganizationOkResponseBody[]
  >([])
  const [billingOrganizations, setBillingOrganizations] = useState<
      OrganizationGetOrganizationOkResponseBody[]
  >([])
  const [superuserUsername, setSuperuserUsername] = useState('')
  const [superuserPassword, setSuperuserPassword] = useState('')
  const noOrganization = { label: 'None', value: '' }
  const priceList = [
    { label: '', value: '' },
    { label: 'Standard', value: 'Standard' },
    { label: 'Discount', value: 'Discount' },
    { label: 'NonBilled', value: 'NonBilled' },
  ]
  const organizationsForList = [
    noOrganization,
    ...allOrganizations
        .filter((org) => hiddenFilter(org.id))
        .map((org) => ({ label: org.name, value: org.id })),
  ]
  const [targetOrganization, setTargetOrganization] = useState('')
  const [priceScheme, setPriceScheme] = useState('')
  const [billingOrgId, setBillingOrgId] = useState('self')
  const [showPricing, setShowPricing] = useState(false)

  const billingOrgOptions = [
    { label: 'Self', value: 'self' },
    ...billingOrganizations
        .filter((org) => hiddenFilter(org.id))
        .map((org) => ({ label: org.name, value: org.id })),
  ]

  const fetchBillingOrgs = async () => {
    try {
      const { data } = await superuserGetAllBillingOrgs({
        superuserUsername,
        superuserPassword,
      })
      setBillingOrganizations(data)
    } catch (err) {
      console.log('Failed to fetch billing orgs', err)
      Alerts.error('Unable to fetch billing organizations')
    }
  }

  const handleOpenPricingModal = async () => {
    setShowPricing(true)
    setBillingOrgId('self')
    setPriceScheme('')
    await fetchBillingOrgs()
  }

  const handleSuperuserGetJWT = async () => {
    const organizationId = targetOrganization
    if (organizationId === '') return
    setLoading(true)
    try {
      const {
        data: { userId, sessionKey, token },
      } = await SuperuserGetOrgOwnerJwt({
        superuserUsername,
        superuserPassword,
        organizationId,
      })

      console.log('Login success', { userId, sessionKey, token })
      setSuperuserPassword('')
      setSuperuserUsername('')
      onComplete(token)
    } catch (err) {
      console.log('Login err', err)
      Alerts.error('Unable to authenticate try again')
    } finally {
      setLoading(false)
    }
  }

  const handleOrgDelete = async () => {
    const organizationId = targetOrganization
    if (organizationId === '') return
    if (!window.confirm('ARE YOU ABSOLUTELY SURE YOU WANT TO DELETE THIS ORG?'))
      return
    if (!window.confirm('This ENTIRE ORG WILL BE GONE.  ARE YOU SURE?')) return
    setLoading(true)
    try {
      await SuperuserDeactivateOrg({
        superuserUsername,
        superuserPassword,
        organizationId,
      })
      setSuperuserPassword('')
      setSuperuserUsername('')
      window.location.reload()
    } catch (err) {
      console.log('Login err', err)
      Alerts.error('Unable to delete try again')
    } finally {
      setLoading(false)
    }
  }

  const handleOrgSetPricing = async () => {
    const organizationId = targetOrganization
    if (organizationId === '') return
    setLoading(true)
    try {
      const pricingParams: {
        superuserUsername: string
        superuserPassword: string
        organizationId: string
        pricing: string
        externalOrganizationId?: string
      } = {
        superuserUsername,
        superuserPassword,
        organizationId,
        pricing: priceScheme,
      }

      // Only add externalOrganizationId if a billing org other than 'self' is selected
      if (billingOrgId !== 'self') {
        pricingParams.externalOrganizationId = billingOrgId
      }

      await SuperuserSetPricing(pricingParams)
      setSuperuserPassword('')
      setSuperuserUsername('')
      window.location.reload()
    } catch (err) {
      console.log('Login err', err)
      Alerts.error('Unable to set pricing try again')
    } finally {
      setLoading(false)
    }
  }

  const handleSuperuserGetOrgs = async () => {
    setLoading(true)
    try {
      const { data } = await SuperuserGetAllOrgs({
        superuserUsername,
        superuserPassword,
      })
      setAllOrganizations(data)
    } catch (err) {
      console.log('Login err', err)
      Alerts.error('Unable to authenticate try again')
    } finally {
      setLoading(false)
    }
  }
  return (
      <>
        {showPricing && (
            <Modal
                closeHandler={() => setShowPricing(false)}
                escPressClose
                header="Choose Pricing"
            >
              <div className="mb-5">
                <Select
                    label="Select Billing Type"
                    onChangeValue={setPriceScheme}
                    options={priceList}
                    value={priceScheme}
                />
              </div>
              <div className="mb-5">
                <Select
                    label="Choose Billing Org"
                    onChangeValue={setBillingOrgId}
                    options={billingOrgOptions}
                    value={billingOrgId}
                />
              </div>
              <Button onClick={handleOrgSetPricing}>Set</Button>
            </Modal>
        )}
        <span />
        {allOrganizations.length > 0 ? (
            <>
              <Select
                  label="Select Organization"
                  onChangeValue={setTargetOrganization}
                  options={organizationsForList}
                  value={targetOrganization}
              />
              <Button onClick={handleSuperuserGetJWT} fullWidth variant="main">
                Login As
              </Button>
              <Button
                  onClick={handleOpenPricingModal}
                  fullWidth
                  variant="main"
              >
                Set Pricing
              </Button>
              <Button onClick={handleOrgDelete} fullWidth variant="main">
                Delete
              </Button>
            </>
        ) : (
            <>
              <Text
                  placeholder="Username"
                  onChangeValue={setSuperuserUsername}
                  value={superuserUsername}
              />
              <Password
                  placeholder="Password"
                  onChangeValue={setSuperuserPassword}
                  value={superuserPassword}
              />
              <Button onClick={handleSuperuserGetOrgs} fullWidth variant="main">
                Superuser Login
              </Button>
            </>
        )}
      </>
  )
}

SuperuserLogin.defaultProps = {}

export { SuperuserLogin }
